// ./src/pages/private/EventMessageComposer/SendCostSummary.tsx
//
// What this send will COST, shown BEFORE the send button.
//
// ⚠ Three rules from MONETIZATION.md §4.2 are load-bearing here, and each of them exists
// because the obvious version of this component is worse than showing nothing:
//
//   1. NEVER say "segment". It is our word for a 160-character billing unit; it means
//      nothing to a host, and a price quoted in a unit the buyer cannot count is the exact
//      thing that makes variable pricing feel like a trap. Say "counts as 2 per person".
//   2. NAME the expensive character. "Your message is long" is not actionable; "the 🎉 makes
//      each message count double — remove it or trim 40 characters" is.
//   3. Show it BEFORE the button, not after the send. A number nobody saw coming is a
//      dispute; the same number seen at the moment of decision is a price.
//
// ⚠ `costByZone` counts RECIPIENTS per zone, not units — the server prices the whole send
// into `costMessages` because a zone's unit cost also depends on the body's length, which is
// a property of the send and not of the zone. Presenting a per-zone cost here would be
// stating something nobody calculated.
import React from "react";
import { Alert, Anchor, Badge, Group, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppIcon } from "@/components/generic";
import { InvitationSendResult } from "@/utils/api/EventGuestService";
import { MessagingBalance } from "@/hooks/useTopUp";

/**
 * Destination zones from eventsmagic-api `src/boundary/messageCost.ts`, in customer words.
 *
 * ⚠ THE KEYS ARE 'A'|'B'|'C'|'D'. An earlier version of this file mapped 'na'/'eu'/'row' —
 * keys that have never existed anywhere in the codebase — so every badge fell through to the
 * raw value and hosts were shown **"2 · D"** and **"1 · A"**, which mean nothing to anyone.
 * I wrote that map from an assumption and wrote a confident comment saying where it came
 * from, without opening the file it named.
 *
 * The multiplier is part of the label on purpose: a zone is only interesting because it costs
 * more, and "Rest of world" alone does not explain why 2 recipients cost 8 units.
 */
const ZONE_LABELS: Record<string, { name: string; multiplier: number }> = {
  A: { name: "US & Canada", multiplier: 1 },
  B: { name: "UK, Ireland, Australia, NZ, Italy", multiplier: 2 },
  C: { name: "Western Europe", multiplier: 3 },
  D: { name: "Rest of world", multiplier: 4 },
};

/**
 * ⚠ Zone D also catches any number whose COUNTRY COULD NOT BE READ, at the most expensive
 * rate. That is deliberate on the server (price defensively rather than lose money), but it
 * means a host can be charged 4x for a local number that simply is not stored in +E.164 — so
 * when D is present the UI says what D actually is, rather than leaving it as a place name.
 */
const UNKNOWN_ZONE = "D";

export interface SendCostSummaryProps {
  preview: InvitationSendResult | null;
  channel: "email" | "sms" | "whatsapp";
  /**
   * Opens the top-up drawer.
   *
   * ⚠ Offered BEFORE the send, not only after a refusal. MONETIZATION §4.2 puts the purchase
   * "inside the composer, where the cost is actually felt" — a host looking at a number they
   * think is too close to their limit should be able to act on it without first being blocked.
   */
  onTopUp?: () => void;
  /**
   * What this send can actually draw on — the DENOMINATOR.
   *
   * ⚠ WITHOUT THIS THE COST LINE IS NOT INFORMATION. "This send uses 101 of your email
   * allowance" tells a host nothing they can act on: 101 of 250 is fine, 101 of 120 is a
   * decision, 101 of 50 is a wasted afternoon. It shipped that way once and was wrong.
   *
   * ⚠ `undefined` means NOT KNOWN and must read as unknown — never as zero, which would say
   * "you have been cut off" to someone with a full allowance.
   */
  balance?: MessagingBalance;
}

export const SendCostSummary: React.FC<SendCostSummaryProps> = ({ preview, channel, onTopUp, balance }) => {
  const { t } = useTranslation(["events", "common"]);

  if (!preview) {
    return null;
  }

  const isEmail = channel === "email";
  const units = isEmail ? preview.costEmails ?? 0 : preview.costMessages ?? 0;

  if (!units) {
    return null;
  }

  // ⚠ EMAIL COSTS 1 PER RECIPIENT, WHATEVER THE BODY. `quoteSend` returns `emails: recipients`
  // for the email channel but still measures the body's length unconditionally, so
  // `countsPerRecipient` comes back as (say) 4 for a long email — a number that is real but
  // means nothing about what an email costs. Measured on 2026-09-10: a ~500-character email
  // body reports `countsPerRecipient: 4` with `costEmails` unaffected.
  //
  // Showing it would tell a host "long enough to count as 4 per person" about a send that
  // costs 1 each — false, and false about money, which is the one thing this component exists
  // to get right. Length and encoding only mean something on the metered rails (sms/whatsapp).
  const perPerson = isEmail ? 1 : preview.countsPerRecipient ?? 1;
  const offending = isEmail ? null : preview.expensiveCharacter;
  const zones = (preview.costByZone ?? []).filter((z) => !!z?.zone && (z.recipients ?? 0) > 0);
  const recipientsCounted = zones.reduce((sum, z) => sum + (z.recipients ?? 0), 0);
  const hasUnknownZone = zones.some((z) => String(z.zone).toUpperCase() === UNKNOWN_ZONE);
  // Only show the working when it actually explains something the reader cannot already see:
  // one zone at ×1 with one unit each needs no arithmetic.
  const showWorking = recipientsCounted > 0 && units !== recipientsCounted * perPerson;

  // ⚠ The available figure and whether this send FITS. `totalAvailable` is null when the
  // allowance is unlimited or could not be read — both mean "do not draw a scary conclusion".
  const available = balance?.totalAvailable ?? null;
  const unlimited = !!balance?.unlimited;
  const short = available != null && units > available;

  const unitWord = isEmail ? "emails" : "messages";
  const headline =
    available != null
      ? t("events:composer.cost.ofAvailable", {
          defaultValue: "This send uses {{count}} of your {{available}} remaining {{unit}}.",
          count: units,
          available: available.toLocaleString(),
          unit: unitWord,
        })
      : unlimited
        ? t("events:composer.cost.unlimited", {
            defaultValue: "This send uses {{count}} {{unit}}. Your plan has no monthly limit.",
            count: units,
            unit: unitWord,
          })
        : isEmail
          ? t("events:composer.cost.emails", {
              defaultValue: "This send uses {{count}} of your email allowance.",
              count: units,
            })
          : t("events:composer.cost.messages", {
              defaultValue: "This send uses {{count}} of your message allowance.",
              count: units,
            });

  return (
    <Alert
      color={short ? "red" : perPerson > 1 || offending ? "yellow" : "blue"}
      variant="light"
      radius="md"
      icon={<AppIcon iconName={isEmail ? "Mail" : "DeviceMobile"} size={18} />}
    >
      <Stack gap={6}>
        <Text size="md" fw={600}>
          {headline}
        </Text>

        {/* ⚠ "counts as N per person" — the plain-language stand-in for a segment count. */}
        {perPerson > 1 ? (
          <Text size="md">
            {t("events:composer.cost.perPerson", {
              defaultValue: "Your message is long enough to count as {{count}} per person.",
              count: perPerson,
            })}{" "}
            {offending
              ? t("events:composer.cost.offendingChar", {
                  // ⚠ The character is QUOTED and the sentence uses no dashes of its own. The
                  // previous copy read: The — is what makes it expensive — removing it lets far
                  // more text fit. When the offending character is itself a dash or a quote it
                  // collided with the punctuation around it and the sentence became unreadable —
                  // which is the one situation this line exists for.
                  defaultValue:
                    'Remove the "{{char}}" and far more text fits before the cost goes up.',
                  char: offending,
                })
              : t("events:composer.cost.trimHint", {
                  defaultValue: "Trimming it will bring the cost down.",
                })}
          </Text>
        ) : offending ? (
          <Text size="md">
            {t("events:composer.cost.offendingCharOnly", {
              defaultValue:
                'Watch the "{{char}}": one more character like it and each message counts double.',
              char: offending,
            })}
          </Text>
        ) : null}

        {zones.length > 0 && (
          <Group gap="xs" wrap="wrap">
            <Text size="xs" c="dimmed">
              {t("events:composer.cost.goingTo", { defaultValue: "Going to:" })}
            </Text>
            {zones.map((z) => {
              const key = String(z.zone).toUpperCase();
              const zone = ZONE_LABELS[key];
              const label = zone ? zone.name : key;
              // ⚠ Name the multiplier where it is more than 1. "3 · Western Europe" still does
              // not tell a host why their 6-unit send became 18.
              const suffix = zone && zone.multiplier > 1 ? ` (×${zone.multiplier})` : "";

              return (
                <Badge key={key} size="md" variant="light" radius="sm">
                  {`${z.recipients} · ${label}${suffix}`}
                </Badge>
              );
            })}
          </Group>
        )}

        {/* ⚠ Says it BEFORE the send button, not after a refusal. A host who can see they are
            short can fix it in two clicks; one who finds out at send time has already
            committed to the moment. */}
        {short && (
          <Text size="md" fw={600}>
            {t("events:composer.cost.short", {
              defaultValue: "That is {{count}} more than you have left — top up to send this.",
              count: units - (available ?? 0),
            })}
          </Text>
        )}

        {balance?.credits != null && balance.credits > 0 && (
          <Text size="xs" c="dimmed">
            {t("events:composer.cost.creditsNote", {
              defaultValue: "Includes {{count}} purchased {{unit}}, which are used first and never expire.",
              count: balance.credits,
              unit: unitWord,
            })}
          </Text>
        )}

        {onTopUp && (
          <Anchor component="button" type="button" size="md" onClick={onTopUp} ta="left">
            {t("events:composer.cost.topUpLink", { defaultValue: "Need more? Buy a bundle" })}
          </Anchor>
        )}

        {/*
          ⚠ THE ARITHMETIC HAS TO BE FOLLOWABLE. A host who sees "3 recipients" and "2 per
          person" expects 6, and then reads 18 with nothing on screen bridging the two — the
          destination multiplier was applied invisibly. A price the buyer cannot reproduce is
          the exact thing MONETIZATION §4.2 says makes variable pricing feel like a trap.
        */}
        {!isEmail && showWorking && (
          <Text size="xs" c="dimmed">
            {t("events:composer.cost.working", {
              defaultValue:
                "{{people}} recipients × {{perPerson}} per person × the destination rate above = {{total}}.",
              people: recipientsCounted,
              perPerson,
              total: units,
            })}
          </Text>
        )}

        {hasUnknownZone && (
          <Text size="xs" c="dimmed">
            {/* ⚠ The most expensive tier is also the fallback for "we could not tell where this
                number is". Saying so is the difference between a host fixing their data and a
                host believing we overcharge. */}
            {t("events:composer.cost.unknownZone", {
              defaultValue:
                'Numbers stored without a country code are priced at the "Rest of world" rate. Saving them in full international form (+1 …) is usually cheaper.',
            })}
          </Text>
        )}
      </Stack>
    </Alert>
  );
};
